import React, { useMemo, useState } from 'react';
import { apiFetch, parseJsonSafe } from '../utils/api';

const parseUrlsFromCsv = (text) => {
  if (!text) return [];
  const lines = text.split(/\r?\n/);
  const urls = [];
  const urlRegex = /(https?:\/\/[^\s,;]+)/i;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    let candidate = '';
    const match = trimmed.match(urlRegex);
    if (match?.[1]) {
      candidate = match[1];
    } else {
      const parts = trimmed.split(/[,\t;]+/).map((p) => p.trim()).filter(Boolean);
      candidate = parts[0] || '';
    }
    if (!candidate) continue;
    candidate = candidate.replace(/^"+|"+$/g, '');
    if (!/^https?:\/\//i.test(candidate)) {
      candidate = `https://${candidate}`;
    }
    try {
      const parsed = new URL(candidate);
      urls.push(parsed.toString());
    } catch (_err) {
      // ignore invalid URLs
    }
  }
  return urls;
};

const ObtenerEmailsPage = () => {
  const token = localStorage.getItem('token');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [fileName, setFileName] = useState('');

  const allSelected = useMemo(() => entries.length > 0 && entries.every((e) => e.selected), [entries]);
  const selectedCount = useMemo(() => entries.filter((e) => e.selected).length, [entries]);

  const addEntries = (urls) => {
    if (!urls.length) return;
    setEntries((prev) => {
      const existing = new Set(prev.map((e) => e.url));
      const next = [...prev];
      urls.forEach((url) => {
        if (existing.has(url)) return;
        next.push({
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          url,
          email: '',
          status: 'pendiente',
          selected: true,
          error: ''
        });
      });
      return next;
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const urls = parseUrlsFromCsv(text);
      addEntries(urls);
    };
    reader.readAsText(file);
  };

  const toggleSelectAll = () => {
    setEntries((prev) => prev.map((entry) => ({ ...entry, selected: !allSelected })));
  };

  const toggleSelect = (id) => {
    setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, selected: !entry.selected } : entry)));
  };

  const updateEntry = (id, patch) => {
    setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  };

  const fetchEmailsSelected = async () => {
    if (!selectedCount) return;
    setBulkAction('email');
    setLoading(true);
    try {
      for (const entry of entries) {
        if (!entry.selected) continue;
        updateEntry(entry.id, { status: 'buscando', error: '' });
        const res = await apiFetch('/api/cb/email-scan', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: JSON.stringify({ url: entry.url })
        });
        const data = await parseJsonSafe(res);
        if (!res.ok) {
          updateEntry(entry.id, { status: 'error', error: data?.message || 'Error obteniendo email' });
        } else {
          updateEntry(entry.id, {
            status: data?.email ? 'encontrado' : 'sin_email',
            email: data?.email || '',
            error: ''
          });
        }
      }
    } finally {
      setLoading(false);
      setBulkAction(null);
    }
  };

  const clearEntries = () => {
    setEntries([]);
    setFileName('');
  };

  return (
    <div className="container">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-1">Obtener emails</h2>
          <div className="text-muted small">
            Sube un CSV con URLs y obtén emails encontrados en la web o páginas de contacto.
          </div>
        </div>
        <div className="d-flex gap-2 mt-2 mt-md-0">
          <button className="btn btn-outline-secondary btn-sm" onClick={clearEntries} disabled={!entries.length || loading}>
            Limpiar lista
          </button>
          <button className="btn btn-success btn-sm" onClick={fetchEmailsSelected} disabled={!selectedCount || loading}>
            {bulkAction === 'email' ? 'Obteniendo emails...' : `Obtener emails (${selectedCount})`}
          </button>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-6">
              <label className="form-label">Importar CSV</label>
              <input type="file" className="form-control" accept=".csv,text/csv" onChange={handleFileChange} />
              {fileName && <div className="text-muted small mt-1">Archivo cargado: {fileName}</div>}
            </div>
            <div className="col-md-6">
              <div className="alert alert-light small mb-0">
                El CSV puede contener una columna con URLs. Si no incluye protocolo, se añadirá https:// automáticamente.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input type="checkbox" className="form-check-input" checked={allSelected} onChange={toggleSelectAll} />
                  </th>
                  <th>URL</th>
                  <th>Email</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {!entries.length && (
                  <tr>
                    <td colSpan="4" className="text-muted small">No hay URLs importadas.</td>
                  </tr>
                )}
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={entry.selected}
                        onChange={() => toggleSelect(entry.id)}
                      />
                    </td>
                    <td className="small">{entry.url}</td>
                    <td className="small">{entry.email || '-'}</td>
                    <td className="small">
                      {entry.status === 'pendiente' && <span className="badge text-bg-secondary">Pendiente</span>}
                      {entry.status === 'buscando' && <span className="badge text-bg-info">Buscando</span>}
                      {entry.status === 'encontrado' && <span className="badge text-bg-success">Email encontrado</span>}
                      {entry.status === 'sin_email' && <span className="badge text-bg-warning">Sin email</span>}
                      {entry.status === 'error' && <span className="badge text-bg-danger">Error</span>}
                      {entry.error && <div className="text-danger small mt-1">{entry.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObtenerEmailsPage;
